import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import './Profile.css';
import avatar from '../assets/avatar.jpg';
import { updateUser } from '../api/userApi';
import { uploadImage, UploadImageResponse } from '../api/imageApi';

interface ProfileFormData {
  id: number;
  firstname: string;
  lastname: string;
  email: string;
  phoneNumber: string;
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
  avatarSrc: File | string | null;
}

const Profile = () => {
  const { user, setUser } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  console.log('user', user);
  const [formData, setFormData] = useState<ProfileFormData>({
    id: 0,
    firstname: '',
    lastname: '',
    email: '',
    phoneNumber: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    avatarSrc: null,
  });

  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        id: user.id || 0,
        firstname: user.firstname || '',
        lastname: user.lastname || '',
        email: user.email || '',
        phoneNumber: user.phone_number || '',
        currentPassword: '********',
      }));
      setIsLoading(false);
    }
  }, [user]);


  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setMessage({ type: 'error', text: 'Please select an image file' });
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'Image size should be less than 10MB' });
      return;
    }

    try {
      setMessage(null);
      setIsLoading(true);
      const response = await uploadImage(file, { type: 'profile' });
      if (response && response.image_url) {
        const imgUrl: string = response.image_url;
        setFormData(prev => ({ ...prev, avatarSrc: imgUrl }));
        setPreviewUrl(response.image_url);
        setMessage({ type: 'success', text: 'Profile picture updated successfully' });
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error: any) {
      console.error('Error uploading image:', error);
      let errorMessage = 'Failed to upload profile picture. Please try again.';
      if (error.response?.status === 413) {
        errorMessage = 'Image is too large. Please select a smaller image.';
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      }
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setIsLoading(false);
    }
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);
    
    console.log('formData', formData);

    // Password validation
    if (formData.newPassword || formData.confirmPassword) {
      if (!formData.newPassword || !formData.confirmPassword) {
        setMessage({ type: 'error', text: 'Please fill both new password fields' });
        return;
      }

      if (formData.newPassword !== formData.confirmPassword) {
        setMessage({ type: 'error', text: 'New passwords do not match' });
        return;
      }

      // Password constraints validation
      // const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d]{8,}$/;
      // if (!passwordRegex.test(formData.newPassword)) {
      //   setMessage({ 
      //     type: 'error', 
      //     text: 'Password must be at least 8 characters long and contain at least one uppercase letter, one lowercase letter, and one number' 
      //   });
      //   return;
      // }
    }

    try {
      // Prepare the update data
      const updateData: any = {
        id: user?.id,
        firstname: formData.firstname,
        lastname: formData.lastname,
        email: formData.email,
        phoneNumber: formData.phoneNumber,
        avatarSrc: formData.avatarSrc,
        username: user?.username
      };

      console.log('updateData', updateData);

      if (formData.newPassword) {
        updateData.currentPassword = formData.currentPassword;
        updateData.newPassword = formData.newPassword;
      }

      const updatedUser = await updateUser(updateData);
      if (updatedUser) {
        // Convert the updated user to the format expected by AuthContext
        const authUser = {
          id: updatedUser.id,
          name: `${updatedUser.firstname} ${updatedUser.lastname}`,
          username: user?.username || '',
          firstname: updatedUser.firstname,
          lastname: updatedUser.lastname,
          avatarSrc: updatedUser.avatarSrc,
          email: updatedUser.email,
          phone_number: updatedUser.phone_number,
          role: updatedUser.role
        };
        console.log('Setting updated user:', authUser);
        setUser(authUser);
        setMessage({ type: 'success', text: 'Profile updated successfully' });
      } else {
        setMessage({ type: 'error', text: 'Failed to update profile' });
      }
    } catch (error) {
      console.error('Update error:', error);
      setMessage({ type: 'error', text: 'Failed to update profile' });
    }
  };

  return (
    <div className="profile-container">
      <h1>Perfil</h1>
      {message && (
        <div className={`message ${message.type}`}>
          {message.text}
        </div>
      )}
      <form onSubmit={handleSubmit} className="profile-form">
        <div className="form-row">
          <div className="form-col">
            <div className="profile-picture-section">
              <div className="profile-picture-preview">
                {isLoading ? (
                  <div className="profile-picture-skeleton" />
                ) : (
                  <img 
                    src={previewUrl || user?.avatarSrc || avatar} 
                    alt="Profile" 
                  />
                )}
              </div>
              <div className="profile-picture-upload">
                <label htmlFor="profilePicture" className="file-input-label">
                  Cambiar foto de perfil
                </label>
                <input
                  type="file"
                  id="profilePicture"
                  name="profilePicture"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="file-input"
                />
              </div>
            </div>
          </div>
          <div className="form-col">
            <div className="form-group">
              <label htmlFor="firstname">Nombre</label>
              <input
                type="text"
                id="firstname"
                name="firstname"
                value={formData.firstname}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="lastname">Apellido</label>
              <input
                type="text"
                id="lastname"
                name="lastname"
                value={formData.lastname}
                onChange={handleInputChange}
                required
              />
            </div>
          </div>
        </div>

        <div className="form-row">
          <div className="form-col">
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
              />
            </div>
          </div>
        </div>

        <div className="form-row">
          <div className="form-col">
            <div className="form-group">
              <label htmlFor="currentPassword">Contraseña actual</label>
              <input
                type="password"
                id="currentPassword"
                name="currentPassword"
                value={formData.currentPassword}
                onChange={handleInputChange}
              />
            </div>
          </div>
          <div className="form-col">
            <div className="form-group">
              <label htmlFor="newPassword">Nueva contraseña</label>
              <input
                type="password"
                id="newPassword"
                name="newPassword"
                value={formData.newPassword}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group">
              <label htmlFor="confirmPassword">Confirmar nueva contraseña</label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
              />
            </div>
          </div>
        </div>

        <button type="submit" className="submit-button">
          Guardar cambios
        </button>
      </form>
    </div>
  );
};

export default Profile; 